import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { DesktopMode, MobileMode } from 'layout'
import {
    TICKET_STATUS,
    TICKET_PRIORITY,
    formatTicketDateTime,
    ticketMatchesSearch,
    ticketMatchesEscalationFilter,
} from 'page-components/auto-ticket/utils'
import { EscalationCell } from 'page-components/auto-ticket/EscalationBadges'
import {
    SearchInput,
    TicketTableWrap,
    TicketTableScroll,
    TicketTable,
    TicketStatusPill,
    TicketPriorityPill,
    TicketMobileList,
    TicketMobileCard,
    TicketMobileTop,
    TicketMobileId,
    TicketMobileMeta,
    TicketMobileTitle,
    TicketMobileFooter,
    TipCard,
    ToolbarRow,
    TicketCountText,
} from './styles'

const statusStyle = statusId => {
    const meta = TICKET_STATUS[statusId] || TICKET_STATUS[1]
    const map = {
        open: { bg: '#f1f5f9', color: '#475569' },
        ack: { bg: '#dbeafe', color: '#1d4ed8' },
        progress: { bg: '#fef3c7', color: '#b45309' },
        resolved: { bg: '#dcfce7', color: '#15803d' },
        closed: { bg: '#f4f4f5', color: '#52525b' },
        escalated: { bg: '#ffedd5', color: '#c2410c' },
        hold: { bg: '#f3e8ff', color: '#6d28d9' },
        pending: { bg: '#e0e7ff', color: '#3730a3' },
        cancelled: { bg: '#f4f4f5', color: '#52525b' },
    }
    return map[meta.pill] || map.open
}

const priorityStyle = priorityId => {
    const meta = TICKET_PRIORITY[priorityId] || TICKET_PRIORITY[2]
    const map = {
        low: { bg: '#f1f5f9', color: '#475569' },
        medium: { bg: '#fef3c7', color: '#92400e' },
        high: { bg: '#fee2e2', color: '#991b1b' },
        critical: { bg: '#fecaca', color: '#7f1d1d' },
    }
    return map[meta.pill] || map.medium
}

const getTicketDisplayMeta = ticket => {
    const st = statusStyle(ticket.statusId)
    const pr = priorityStyle(ticket.priorityId)
    const statusMeta = TICKET_STATUS[ticket.statusId] || TICKET_STATUS[1]
    const priorityMeta = TICKET_PRIORITY[ticket.priorityId] || TICKET_PRIORITY[2]
    return { st, pr, statusMeta, priorityMeta }
}

const renderTicketRow = (ticket, openTicket) => {
    const { st, pr, statusMeta, priorityMeta } = getTicketDisplayMeta(ticket)

    return (
        <tr key={ticket.ticketId}>
            <td>
                <span
                    className="mono"
                    role="link"
                    tabIndex={0}
                    onClick={() => openTicket(ticket.ticketId)}
                    onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            openTicket(ticket.ticketId)
                        }
                    }}
                >
                    #{ticket.ticketId}
                </span>
            </td>
            <td>
                <TicketStatusPill $bg={st.bg} $color={st.color}>
                    {statusMeta.label}
                </TicketStatusPill>
            </td>
            <td>
                <TicketPriorityPill $bg={pr.bg} $color={pr.color}>
                    {priorityMeta.label}
                </TicketPriorityPill>
            </td>
            <td className="title-cell">{ticket.title || '—'}</td>
            <td>{ticket.ruleId ?? '—'}</td>
            <td>
                <EscalationCell ticket={ticket} />
            </td>
            <td>{formatTicketDateTime(ticket.createdOn)}</td>
            <td>{formatTicketDateTime(ticket.updatedOn)}</td>
        </tr>
    )
}

const renderTicketMobileCard = (ticket, openTicket) => {
    const { st, pr, statusMeta, priorityMeta } = getTicketDisplayMeta(ticket)

    return (
        <TicketMobileCard
            key={ticket.ticketId}
            type="button"
            onClick={() => openTicket(ticket.ticketId)}
        >
            <TicketMobileTop>
                <TicketMobileId>#{ticket.ticketId}</TicketMobileId>
            </TicketMobileTop>
            <TicketMobileMeta>
                <TicketStatusPill $bg={st.bg} $color={st.color}>
                    {statusMeta.label}
                </TicketStatusPill>
                <TicketPriorityPill $bg={pr.bg} $color={pr.color}>
                    {priorityMeta.label}
                </TicketPriorityPill>
            </TicketMobileMeta>
            <TicketMobileTitle>{ticket.title || '—'}</TicketMobileTitle>
            <TicketMobileFooter>
                <div>
                    <span className="label">Rule</span>
                    <span className="value">{ticket.ruleId ?? '—'}</span>
                </div>
                <div>
                    <span className="label">Escalation</span>
                    <span className="value">
                        <EscalationCell ticket={ticket} />
                    </span>
                </div>
                <div>
                    <span className="label">Created</span>
                    <span className="value">
                        {formatTicketDateTime(ticket.createdOn)}
                    </span>
                </div>
                <div>
                    <span className="label">Updated</span>
                    <span className="value">
                        {formatTicketDateTime(ticket.updatedOn)}
                    </span>
                </div>
            </TicketMobileFooter>
        </TicketMobileCard>
    )
}

export const TemplateTicketsPanel = ({
    tickets,
    secureLinkPrefix = '/app/secure/tickets/',
    escalationFilter = null,
}) => {
    const navigate = useNavigate()
    const [search, setSearch] = useState('')

    const filtered = useMemo(
        () =>
            tickets.filter(
                t =>
                    ticketMatchesSearch(t, search) &&
                    ticketMatchesEscalationFilter(t, escalationFilter),
            ),
        [tickets, search, escalationFilter],
    )

    const openTicket = ticketId => {
        navigate(`${secureLinkPrefix}${ticketId}`)
    }

    if (tickets.length === 0) {
        return (
            <TipCard>
                <h2>No tickets yet</h2>
                <p>
                    When auto-ticket rules fire on completed audits, tickets
                    will appear here. Enable rules and complete an audit with
                    matching answers to generate tickets.
                </p>
            </TipCard>
        )
    }

    return (
        <>
            <ToolbarRow>
                <SearchInput
                    type="text"
                    placeholder="Search by ticket #, title, or rule…"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />
                <div className="spacer" />
                <TicketCountText>
                    {filtered.length} of {tickets.length} ticket
                    {tickets.length === 1 ? '' : 's'}
                </TicketCountText>
            </ToolbarRow>

            {filtered.length === 0 ? (
                <TipCard>
                    <h2>No matches</h2>
                    <p>No tickets match your search.</p>
                </TipCard>
            ) : (
                <TicketTableWrap>
                    <DesktopMode>
                        <TicketTableScroll>
                            <TicketTable>
                                <thead>
                                    <tr>
                                        <th>Ticket</th>
                                        <th>Status</th>
                                        <th>Priority</th>
                                        <th>Title</th>
                                        <th>Rule</th>
                                        <th>Escalation</th>
                                        <th>Created</th>
                                        <th>Updated</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.map(ticket =>
                                        renderTicketRow(ticket, openTicket),
                                    )}
                                </tbody>
                            </TicketTable>
                        </TicketTableScroll>
                    </DesktopMode>
                    <MobileMode>
                        <TicketMobileList>
                            {filtered.map(ticket =>
                                renderTicketMobileCard(ticket, openTicket),
                            )}
                        </TicketMobileList>
                    </MobileMode>
                </TicketTableWrap>
            )}
        </>
    )
}
